/**
 * Per-driver provider field schema — the single source of truth for which
 * `provider.api.*` fields each SMS driver needs (mandatory vs optional).
 *
 * Provider holds only service-level config. Credentials live on the linked
 * Account (`username` / `password`), described by `account`. Send-time settings
 * (sender name, shorten links, routing) are chosen per Campaign, not here.
 */
export type ProviderFieldType = "text" | "number";

export type ProviderField = {
  key: string; // stored at provider.api[key]
  label: string;
  required: boolean;
  type?: ProviderFieldType; // default "text"
  placeholder?: string;
  help?: string;
};

export type DriverSchema = {
  driver: string; // stored at provider.api.driver; selects the runtime driver
  label: string; // display name
  /** What the linked Account's username/password should hold for this driver. */
  account: { usernameLabel?: string; passwordLabel: string; help?: string };
  fields: ProviderField[];
};

export const DRIVER_SCHEMAS: DriverSchema[] = [
  {
    driver: "messagewhiz",
    label: "MessageWhiz",
    account: { passwordLabel: "API key (apikey)" },
    fields: [
      {
        key: "endpoint",
        label: "Endpoint URL",
        required: false,
        placeholder: "https://sms.messagewhiz.com/sms",
        help: "Defaults to https://sms.messagewhiz.com/sms",
      },
      {
        key: "callback",
        label: "Callback URL",
        required: false,
        placeholder: "https://your.site/callback",
      },
    ],
  },
  {
    driver: "smsedge",
    label: "SMSEdge",
    account: { passwordLabel: "API key (api_key)" },
    fields: [
      {
        key: "endpoint",
        label: "Endpoint URL",
        required: false,
        placeholder: "https://api.smsedge.com/v1/sms/send-single/",
      },
      {
        key: "from",
        label: "Default Sender (from)",
        required: false,
        placeholder: "INFO",
      },
      { key: "country_id", label: "Country ID", required: false, type: "number" },
      { key: "list_id", label: "List ID", required: false, type: "number" },
      {
        key: "smart_routing",
        label: "Smart routing",
        required: false,
        type: "number",
        placeholder: "0",
      },
      {
        key: "transactional",
        label: "Transactional",
        required: false,
        type: "number",
        placeholder: "0",
      },
    ],
  },
  {
    driver: "twilio",
    label: "Twilio",
    account: {
      usernameLabel: "Account SID",
      passwordLabel: "Auth Token",
    },
    fields: [
      {
        key: "status_callback",
        label: "Status callback URL",
        required: false,
        placeholder: "https://your.site/status",
      },
      {
        key: "endpoint",
        label: "Base URL override",
        required: false,
        placeholder: "https://api.twilio.com",
      },
    ],
  },
  {
    driver: "mamonty",
    label: "Mamonty (P1SMS)",
    account: { passwordLabel: "API key (apiKey)" },
    fields: [
      {
        key: "channel",
        label: "Channel",
        required: true,
        placeholder: "char",
        help: "digit | char | viber | vk | whatsapp | wa_api | telegram_auth",
      },
      {
        key: "webhookUrl",
        label: "Status webhook URL",
        required: false,
        placeholder: "https://your.site/webhook",
      },
      {
        key: "clickWebhookUrl",
        label: "Click webhook URL",
        required: false,
        placeholder: "https://your.site/click",
      },
      {
        key: "endpoint",
        label: "Endpoint URL",
        required: false,
        placeholder: "https://admin.mamonty.com/apiSms/create",
      },
    ],
  },
];

export function getDriverSchema(driver: string | undefined): DriverSchema | undefined {
  return DRIVER_SCHEMAS.find((s) => s.driver === driver);
}
